/**
 * Person Validation Schemas
 *
 * Centralized Zod schemas for person-related validation across the application.
 * Used by AddPersonModal, RightDetailsPanel, and onboarding flows.
 *
 * @module lib/validation/person.schema
 */

import { z } from 'zod';
import i18next from 'i18next';

/**
 * Gender enum schema
 */
export const genderSchema = z.enum(['male', 'female', 'other'], {
  errorMap: () => ({ message: (i18next.t as any)('validation.genderInvalid', { ns: 'common' }) }),
});

/**
 * Marital status enum schema
 */
export const maritalStatusSchema = z.enum(['single', 'married', 'divorced', 'widowed']).nullable();

/**
 * Base person data schema (common fields)
 * Includes only the core fields required for person creation/update
 */
export const basePersonSchema = z.object({
  first_name: z
    .string()
    .min(1, { message: (i18next.t as any)('tree.errors.firstNameRequired', { ns: 'common' }) })
    .refine((val) => val.trim().length > 0, {
      message: (i18next.t as any)('tree.errors.firstNameRequired', { ns: 'common' })
    })
    .transform((val) => val.trim())
    .refine((val) => val.length <= 100, {
      message: (i18next.t as any)('validation.firstNameMaxLength', { ns: 'common' })
    }),

  last_name: z
    .string()
    .min(1, { message: (i18next.t as any)('tree.errors.lastNameRequired', { ns: 'common' }) })
    .refine((val) => val.trim().length > 0, {
      message: (i18next.t as any)('tree.errors.lastNameRequired', { ns: 'common' })
    })
    .transform((val) => val.trim())
    .refine((val) => val.length <= 100, {
      message: (i18next.t as any)('validation.lastNameMaxLength', { ns: 'common' })
    }),

  nickname: z
    .string()
    .trim()
    .max(50, { message: (i18next.t as any)('validation.nicknameMaxLength', { ns: 'common' }) })
    .optional()
    .or(z.literal('')),

  gender: genderSchema,

  date_of_birth: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (date) => {
        if (!date) return true; // Optional field
        const birthDate = new Date(date);
        return !isNaN(birthDate.getTime());
      },
      { message: (i18next.t as any)('validation.invalidDate', { ns: 'common' }) }
    ),

  date_of_death: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (date) => {
        if (!date) return true; // Optional field
        const deathDate = new Date(date);
        return !isNaN(deathDate.getTime());
      },
      { message: (i18next.t as any)('validation.invalidDate', { ns: 'common' }) }
    ),

  marital_status: maritalStatusSchema.optional(),

  birth_place: z
    .string()
    .trim()
    .max(200, { message: (i18next.t as any)('validation.birthPlaceMaxLength', { ns: 'common' }) })
    .optional()
    .or(z.literal('')),

  death_place: z
    .string()
    .trim()
    .max(200, { message: (i18next.t as any)('validation.deathPlaceMaxLength', { ns: 'common' }) })
    .optional()
    .or(z.literal('')),

  notes: z
    .string()
    .trim()
    .max(2000, { message: (i18next.t as any)('validation.notesMaxLength', { ns: 'common' }) })
    .optional()
    .or(z.literal('')),
});

/**
 * Person data schema with date range validation
 * Refines the base schema to ensure death date is after birth date
 */
export const personDataSchema = basePersonSchema.refine(
  (data) => {
    // If either date is missing, skip validation
    if (!data.date_of_birth || !data.date_of_death) return true;

    const birthDate = new Date(data.date_of_birth);
    const deathDate = new Date(data.date_of_death);

    return deathDate >= birthDate;
  },
  {
    message: (i18next.t as any)('tree.errors.invalidDateRange', { ns: 'common' }),
    path: ['date_of_death'], // Set error on death date field
  }
);

/**
 * Image file validation schema
 * Validates file type and size constraints
 */
export const imageFileSchema = z
  .instanceof(File)
  .refine(
    (file) => file.type.startsWith('image/'),
    { message: (i18next.t as any)('tree.errors.imageSelectError', { ns: 'common' }) }
  )
  .refine(
    (file) => file.size <= 5 * 1024 * 1024, // 5MB max
    { message: (i18next.t as any)('tree.errors.imageSizeError', { ns: 'common' }) }
  )
  .nullable()
  .optional();

/**
 * Complete person form schema (for AddPersonModal)
 * Includes profile image validation
 */
export const personFormSchema = basePersonSchema
  .extend({
    profile_image: imageFileSchema,
  })
  .refine(
    (data) => {
      // If either date is missing, skip validation
      if (!data.date_of_birth || !data.date_of_death) return true;

      const birthDate = new Date(data.date_of_birth);
      const deathDate = new Date(data.date_of_death);

      return deathDate >= birthDate;
    },
    {
      message: (i18next.t as any)('tree.errors.invalidDateRange', { ns: 'common' }),
      path: ['date_of_death'], // Set error on death date field
    }
  );

/**
 * Person update schema (for RightDetailsPanel edit)
 * Same as form schema but all fields optional except first_name
 */
export const personUpdateSchema = basePersonSchema.partial().required({
  first_name: true,
  gender: true,
});

// ==================== TYPE EXPORTS ====================

/**
 * Inferred TypeScript type for person data
 */
export type PersonData = z.infer<typeof personDataSchema>;

/**
 * Inferred TypeScript type for person form data (with image)
 */
export type PersonFormData = z.infer<typeof personFormSchema>;

/**
 * Inferred TypeScript type for person update data
 */
export type PersonUpdateData = z.infer<typeof personUpdateSchema>;

/**
 * Inferred TypeScript type for gender
 */
export type Gender = z.infer<typeof genderSchema>;

/**
 * Inferred TypeScript type for marital status
 */
export type MaritalStatus = z.infer<typeof maritalStatusSchema>;

// ==================== PHASE 2: ENHANCED VALIDATION SCHEMAS ====================

/**
 * Date qualifier schema (GEDCOM 5.5.5 standard)
 */
export const dateQualifierSchema = z.enum(['', 'ABT', 'BEF', 'AFT', 'CAL', 'EST']).nullable().optional();

/**
 * Dual parentage base schema (Phase 2 - Migration 1)
 * Base object without refinements for merging
 */
export const dualParentageBaseSchema = z.object({
  father_id: z.string().uuid().nullable().optional(),
  mother_id: z.string().uuid().nullable().optional(),
  parents_id: z.string().uuid().nullable().optional(),
});

/**
 * Dual parentage validation schema (Phase 2 - Migration 1)
 * Ensures exclusivity: either biological parents OR adoptive parents, not both
 */
export const dualParentageSchema = dualParentageBaseSchema.refine(
  (data) => !data.parents_id || (!data.father_id && !data.mother_id),
  {
    message: 'Cannot have both parents_id and individual father_id/mother_id',
    path: ['parents_id'],
  }
);

/**
 * Flexible date base schema (Phase 2 - Migration 2)
 * Base object without refinements for merging
 */
export const flexibleDateBaseSchema = z.object({
  yob: z.number().int().min(1).max(new Date().getFullYear() + 1).nullable().optional(),
  yod: z.number().int().min(1).max(new Date().getFullYear() + 1).nullable().optional(),
  date_of_birth_qualifier: dateQualifierSchema,
  date_of_death_qualifier: dateQualifierSchema,
});

/**
 * Flexible date validation schema (Phase 2 - Migration 2)
 * Supports year-only dates and GEDCOM date qualifiers
 */
export const flexibleDateSchema = flexibleDateBaseSchema.refine(
  (data) => {
    // If both yob and yod present, yod must be >= yob
    if (data.yob && data.yod) {
      return data.yod >= data.yob;
    }
    return true;
  },
  {
    message: 'Death year must be after or equal to birth year',
    path: ['yod'],
  }
);

/**
 * Person with all Phase 2 enhancements
 * Combines base person data with dual parentage and flexible dates
 */
export const personEnhancedSchema = basePersonSchema
  .merge(dualParentageBaseSchema)
  .merge(flexibleDateBaseSchema)
  .refine(
    (data) => {
      // Validate parentage exclusivity
      return !data.parents_id || (!data.father_id && !data.mother_id);
    },
    {
      message: 'Cannot have both parents_id and individual father_id/mother_id',
      path: ['parents_id'],
    }
  )
  .refine(
    (data) => {
      // Validate year range: yod >= yob
      if (data.yob && data.yod) {
        return data.yod >= data.yob;
      }
      return true;
    },
    {
      message: 'Death year must be after or equal to birth year',
      path: ['yod'],
    }
  )
  .refine(
    (data) => {
      // Validate date range: date_of_death >= date_of_birth
      if (!data.date_of_birth || !data.date_of_death) return true;

      const birthDate = new Date(data.date_of_birth);
      const deathDate = new Date(data.date_of_death);

      return deathDate >= birthDate;
    },
    {
      message: (i18next.t as any)('tree.errors.invalidDateRange', { ns: 'common' }),
      path: ['date_of_death'],
    }
  );

// ==================== TYPE EXPORTS (PHASE 2) ====================

/**
 * Inferred TypeScript type for date qualifier
 */
export type DateQualifier = z.infer<typeof dateQualifierSchema>;

/**
 * Inferred TypeScript type for dual parentage data
 */
export type DualParentageData = z.infer<typeof dualParentageSchema>;

/**
 * Inferred TypeScript type for flexible date data
 */
export type FlexibleDateData = z.infer<typeof flexibleDateSchema>;

/**
 * Inferred TypeScript type for person with all Phase 2 enhancements
 */
export type PersonEnhancedData = z.infer<typeof personEnhancedSchema>;
